import { useEffect } from 'react';
import { useApi } from './useApi';
import { applyThemeCssVars, createTheme } from '@caro/shared/theme';
import type { BuildingBranding } from '@caro/shared/types';

/**
 * Fetches building branding from the API and writes the resolved CARO theme
 * onto `document.documentElement` as CSS custom properties.
 *
 * Phase 2 (T2.1) — Theme single-source-of-truth.
 *   Before: this hook poked individual `--caro-*` properties by hand from the
 *           raw branding payload.
 *   Now:    branding → `createTheme(branding)` → `applyThemeCssVars(theme)`.
 *           `createTheme` is the single source of truth (defaults included),
 *           and `applyThemeCssVars` emits the full token surface — including
 *           the new `--font-small` token (T2.3) — in one place.
 *
 * Falls back to default CARO branding when the API returns no branding (the
 * `createTheme(undefined)` path applies default tokens). Defaults already
 * live in `global.css :root` for the pre-React-mount paint; this bridge
 * overrides them at hydration.
 */
export function useBranding(buildingId: number | undefined) {
  const url = buildingId ? `/api/v1/building/${buildingId}/config` : null;
  const { data } = useApi<{ branding?: BuildingBranding }>(url);

  useEffect(() => {
    // Always apply a theme — even pre-fetch — so the tokens are guaranteed
    // to resolve on documentElement after first mount. `createTheme(undefined)`
    // yields the default CARO theme.
    const theme = createTheme(data?.branding);
    applyThemeCssVars(theme);

    // NOTE (T1.3 / Phase 1): The previous cleanup function that stripped these
    // CSS custom properties on unmount has been intentionally removed.
    // With useBranding lifted to App.tsx root, the only unmount event is full
    // app teardown — at which point the document is being destroyed anyway.
    // Keeping the cleanup would (a) misfire under React Strict Mode's
    // double-invoke in dev, and (b) caused branding to evaporate during
    // navigation when the hook was page-scoped (finding #3).
  }, [data]);

  return data?.branding ?? null;
}
